/**
 * Initializes animations for animation block
 *
 * How to use:
 *
 * <!-- Animation container - controls group / children / target animation settings -->
 * <div data-animation-type="fade-in-up"          // Animation type (DEFAULT_ANIMATIONS)
 *      data-animation-offset="100"               // Scroll offset (px)
 *      data-animation-duration="0.8"             // Override default duration (s)
 *      data-animation-stagger="0.2"              // Time between element/target animations (s)
 *      data-animation-from-x="50"                // Animation trigger starting x position (px)
 *      data-animation-from-y="20">               // Animation trigger starting y position (px)
 *
 *     <!-- Animated elements -->
 *     <div data-animation-target></div>          // Will be animated
 *     <div data-animation-target></div>          // Will be animated
 *     <div></div>                                // Will not be animated
 * </div>
 *
 */
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);
const DEFAULT_ANIMATIONS = {
    'slide-in-left': {
        from: { x: -100, opacity: 0 },
        to: { x: 0, opacity: 1 },
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.1
    },
    'slide-in-right': {
        from: { x: 100, opacity: 0 },
        to: { x: 0, opacity: 1 },
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.1
    },
    'fade-in': {
        from: { opacity: 0 },
        to: { opacity: 1 },
        duration: 0.6,
        ease: 'power1.out',
        stagger: 0.1
    },
    'fade-in-up': {
        from: { y: 50, opacity: 0 },
        to: { y: 0, opacity: 1 },
        duration: 0.7,
        ease: 'power2.out',
        stagger: 0.1
    },
    'fade-in-down': {
        from: { y: -50, opacity: 0 },
        to: { y: 0, opacity: 1 },
        duration: 0.7,
        ease: 'power2.out',
        stagger: 0.1
    },
    'scale-up': {
        from: { scale: 0.8, opacity: 0 },
        to: { scale: 1, opacity: 1 },
        duration: 0.5,
        ease: 'back.out(1.7)',
        stagger: 0.1
    },
    'rotate-in': {
        from: { rotation: -180, opacity: 0 },
        to: { rotation: 0, opacity: 1 },
        duration: 1,
        ease: 'elastic.out(1, 0.5)',
        stagger: 0.1
    }
};
export default class Animate {
    constructor() {
        this.animationBlocks = document.querySelectorAll('[data-animation-type]');
        if (!this.animationBlocks)
            return;
        this.animationBlocks.forEach(animationBlock => this.initAnimationBlock(animationBlock));
    }
    initAnimationBlock(block) {
        // Get all elements to animate within parent
        const animationTargets = block.querySelectorAll('[data-animation-target]');
        if (!animationTargets.length)
            return;
        // Get animation configuration - default to fade-in if type is invalid or empty
        const animationType = (block.dataset.animationType || 'fade-in');
        const animationConfig = DEFAULT_ANIMATIONS[animationType] || DEFAULT_ANIMATIONS['fade-in'];
        // Custom overrides from data attributes
        const { animationOffset: offset, animationDuration: duration, animationStagger: stagger, animationFromX: fromX, animationFromY: fromY } = block.dataset;
        // Calculate values
        const staggerVal = stagger ? parseFloat(stagger) : animationConfig.stagger;
        const durationVal = duration ? Math.abs(parseFloat(duration)) : animationConfig.duration;
        const posYVal = fromY ? parseInt(fromY) : (animationConfig.from.y || 0);
        const posXVal = fromX ? parseInt(fromX) : (animationConfig.from.x || 0);
        // Calculate scroll trigger offsets
        let offsetValue = '';
        if (offset) {
            const offsetNr = parseInt(offset);
            offsetValue = offsetNr >= 0 ? `+=${offsetNr}` : `-=${Math.abs(offsetNr)}`;
        }
        // Set initial state
        const fromState = Object.assign(Object.assign(Object.assign({}, animationConfig.from), (fromX && { x: posXVal })), (fromY && { y: posYVal }) // Add custom Y offset if provided
        );
        gsap.set(animationTargets, fromState);
        // Scroll trigger
        ScrollTrigger.create({
            trigger: block,
            start: `top${offsetValue} center`,
            once: true,
            onEnter: () => {
                gsap.to(animationTargets, Object.assign(Object.assign({}, animationConfig.to), { duration: durationVal, stagger: staggerVal, ease: animationConfig.ease }));
            }
        });
    }
}
